/**
 * Gamification Service
 * Handles streaks, daily missions, peer challenges, and AI personas
 */

import { supabase } from '../lib/supabase';

export const gamificationService = {
  // ==================== STREAKS ====================
  
  /**
   * Update user's streak based on activity
   */
  async updateStreak(activityType = 'general') {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const today = new Date().toISOString().split('T')[0];

      // Get current streak
      let { data: streak } = await supabase
        .from('user_streaks')
        .select('*')
        .eq('user_id', user.id)
        .single();

      const lastActivity = streak?.last_activity_date;
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

      let newStreak = 1;
      let streakContinued = false;

      if (lastActivity === today) {
        // Already counted today
        return streak;
      } else if (lastActivity === yesterday) {
        // Streak continues
        newStreak = (streak?.current_streak || 0) + 1;
        streakContinued = true;
      } else if (streak?.streak_freeze_available && lastActivity < yesterday) {
        // Use streak freeze
        newStreak = streak.current_streak;
        streakContinued = true;
      }

      // Calculate XP bonus
      const xpBonus = this.calculateStreakXP(newStreak, streakContinued);

      // Update or insert
      const { data: updated, error } = await supabase
        .from('user_streaks')
        .upsert({
          user_id: user.id,
          current_streak: newStreak,
          longest_streak: Math.max(newStreak, streak?.longest_streak || 0),
          last_activity_date: today,
          [`${activityType}_streak`]: (streak?.[`${activityType}_streak`] || 0) + 1,
          total_streak_xp_earned: (streak?.total_streak_xp_earned || 0) + xpBonus,
          streak_freeze_available: newStreak % 7 === 0, // Earn freeze every 7 days
        })
        .select()
        .single();

      if (error) throw error;

      return { ...updated, xpEarned: xpBonus, streakContinued };
    } catch (error) {
      console.error('Error updating streak:', error);
      throw error;
    }
  },

  /**
   * Calculate XP bonus for streak (reduced rewards for sustainable progression)
   */
  calculateStreakXP(streakDays, continued) {
    if (!continued) return 3; // New streak (was 10)
    
    // Escalating rewards (reduced by 70%)
    if (streakDays >= 30) return 30; // was 100
    if (streakDays >= 14) return 15; // was 50
    if (streakDays >= 7) return 8;   // was 25
    return 3 + Math.floor(streakDays / 2); // Slower scaling
  },

  /**
   * Get user's current streak
   */
  async getStreak() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_streaks')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data || { current_streak: 0, longest_streak: 0 };
    } catch (error) {
      console.error('Error getting streak:', error);
      return { current_streak: 0, longest_streak: 0 };
    }
  },

  // ==================== DAILY MISSIONS ====================

  /**
   * Get today's missions for user
   */
  async getTodaysMissions() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const today = new Date().toISOString().split('T')[0];

      // Get user's assigned missions
      const { data: userMissions, error: userError } = await supabase
        .from('user_mission_progress')
        .select(`
          *,
          mission:daily_missions(*)
        `)
        .eq('user_id', user.id)
        .eq('assigned_date', today)
        .order('created_at', { ascending: true });

      if (userError) throw userError;

      // If no missions assigned, create daily set
      if (!userMissions || userMissions.length === 0) {
        return await this.assignDailyMissions();
      }

      return userMissions.map(um => ({
        ...um.mission,
        progress: um.progress,
        isCompleted: um.is_completed,
        completedAt: um.completed_at,
        progressId: um.id
      }));
    } catch (error) {
      console.error('Error getting missions:', error);
      throw error;
    }
  },

  /**
   * Assign daily missions to user
   */
  async assignDailyMissions() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get random active missions (3 per day)
      const { data: missions, error: missionsError } = await supabase
        .from('daily_missions')
        .select('*')
        .eq('is_active', true)
        .limit(50);

      if (missionsError) throw missionsError;

      // Select 3 random missions (1 easy, 1 medium, 1 hard)
      const easy = missions.filter(m => m.difficulty === 'easy');
      const medium = missions.filter(m => m.difficulty === 'medium');
      const hard = missions.filter(m => m.difficulty === 'hard');

      const selected = [
        easy[Math.floor(Math.random() * easy.length)],
        medium[Math.floor(Math.random() * medium.length)],
        hard[Math.floor(Math.random() * hard.length)]
      ].filter(Boolean);

      // Assign to user
      const today = new Date().toISOString().split('T')[0];
      const assignments = selected.map(mission => ({
        user_id: user.id,
        mission_id: mission.id,
        assigned_date: today,
        progress: {}
      }));

      const { data: assigned, error: assignError } = await supabase
        .from('user_mission_progress')
        .insert(assignments)
        .select(`
          *,
          mission:daily_missions(*)
        `);

      if (assignError) throw assignError;

      return assigned.map(um => ({
        ...um.mission,
        progress: um.progress,
        isCompleted: false,
        progressId: um.id
      }));
    } catch (error) {
      console.error('Error assigning missions:', error);
      throw error;
    }
  },

  /**
   * Update mission progress
   */
  async updateMissionProgress(progressId, progressUpdate) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current progress
      const { data: current } = await supabase
        .from('user_mission_progress')
        .select('*, mission:daily_missions(*)')
        .eq('id', progressId)
        .eq('user_id', user.id)
        .single();

      if (!current) throw new Error('Mission not found');

      const newProgress = { ...current.progress, ...progressUpdate };
      const isCompleted = this.checkMissionComplete(newProgress, current.mission.requirements);

      const { data: updated, error } = await supabase
        .from('user_mission_progress')
        .update({
          progress: newProgress,
          is_completed: isCompleted,
          completed_at: isCompleted ? new Date().toISOString() : null,
          xp_claimed: isCompleted ? current.mission.xp_reward : 0
        })
        .eq('id', progressId)
        .select()
        .single();

      if (error) throw error;

      return { ...updated, justCompleted: isCompleted && !current.is_completed };
    } catch (error) {
      console.error('Error updating mission progress:', error);
      throw error;
    }
  },

  /**
   * Check if mission requirements are met
   */
  checkMissionComplete(progress, requirements) {
    for (const [key, required] of Object.entries(requirements)) {
      if ((progress[key] || 0) < required) return false;
    }
    return true;
  },

  // ==================== PEER CHALLENGES ====================

  /**
   * Create peer challenge
   */
  async createChallenge({ opponentId, challengeType, rules }) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('peer_challenges')
        .insert({
          challenger_id: user.id,
          opponent_id: opponentId,
          challenge_type: challengeType,
          rules,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating challenge:', error);
      throw error;
    }
  },

  /**
   * Accept/decline challenge
   */
  async respondToChallenge(challengeId, accept) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('peer_challenges')
        .update({
          status: accept ? 'accepted' : 'declined',
          accepted_at: accept ? new Date().toISOString() : null
        })
        .eq('id', challengeId)
        .eq('opponent_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error responding to challenge:', error);
      throw error;
    }
  },

  /**
   * Get user's challenges (sent and received)
   */
  async getChallenges(status = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('peer_challenges')
        .select('*, challenger:challenger_id(id, email), opponent:opponent_id(id, email)')
        .or(`challenger_id.eq.${user.id},opponent_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting challenges:', error);
      throw error;
    }
  },

  // ==================== AI PERSONAS ====================

  /**
   * Get all AI personas
   */
  async getPersonas(includeStats = false) {
    try {
      const { data, error } = await supabase
        .from('ai_personas')
        .select('*')
        .order('popularity_score', { ascending: false });

      if (error) throw error;

      if (includeStats) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: stats } = await supabase
            .from('user_persona_stats')
            .select('*')
            .eq('user_id', user.id);

          return data.map(persona => ({
            ...persona,
            userStats: stats?.find(s => s.persona_id === persona.id) || null
          }));
        }
      }

      return data;
    } catch (error) {
      console.error('Error getting personas:', error);
      throw error;
    }
  },

  /**
   * Unlock persona for user
   */
  async unlockPersona(personaId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_persona_stats')
        .upsert({
          user_id: user.id,
          persona_id: personaId,
          is_unlocked: true,
          unlocked_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error unlocking persona:', error);
      throw error;
    }
  },

  /**
   * Update persona practice stats
   */
  async updatePersonaStats(personaId, score) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current stats
      let { data: current } = await supabase
        .from('user_persona_stats')
        .select('*')
        .eq('user_id', user.id)
        .eq('persona_id', personaId)
        .single();

      if (!current) {
        // Create initial stats
        current = await this.unlockPersona(personaId);
      }

      const newAvg = ((current.total_score_avg * current.times_practiced) + score) / (current.times_practiced + 1);

      const { data, error } = await supabase
        .from('user_persona_stats')
        .update({
          times_practiced: current.times_practiced + 1,
          total_score_avg: Math.round(newAvg),
          best_score: Math.max(current.best_score, score)
        })
        .eq('id', current.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating persona stats:', error);
      throw error;
    }
  }
};

export default gamificationService;
